import { z } from 'zod';

/** Dashboard and revenue-report contracts (docs/spec.md §7 screens 2 and 9). */

export interface DashboardKpis {
  appointmentsToday: number;
  patientsTotal: number;
  /**
   * Collected this month, not billed — matches the billing summary card.
   * Admin sees the clinic total (`revenueScope: 'total'`); every other role
   * sees only what they personally issued/sold/treated (`'own'`) — never the
   * clinic's total earnings (docs/spec.md §14 round 3).
   */
  revenueThisMonth: number;
  revenueScope: 'total' | 'own';
  /** Lab results this user reported this month. Meaningful for the lab role only — 0 otherwise, since lab work isn't priced on its own in this system. */
  labTestsThisMonth: number;
  lowStockCount: number;
}

export interface ActivityPoint {
  date: string;
  /** Appointments scheduled for that day. */
  booked: number;
  /** Of those, how many the patient actually attended. */
  visited: number;
}

export interface ServiceSlice {
  label: string;
  amount: number;
}

export interface LowStockItem {
  id: number;
  nameLa: string;
  unit: string;
  qtyOnHand: number;
  reorderLevel: number;
}

export interface TodayAppointment {
  id: number;
  time: string;
  patientName: string;
  doctorName: string | null;
  status: string;
}

export interface ActivityEntry {
  id: number;
  action: string;
  entity: string;
  entityId: string | null;
  userName: string | null;
  createdAt: string;
}

export interface Dashboard {
  kpis: DashboardKpis;
  /** Oldest first, one entry per day for the last 14 days. */
  activity: ActivityPoint[];
  /**
   * Billed amounts per service line, not collected — a partial payment cannot
   * be attributed to particular lines, so the two are kept separate rather
   * than blended into a misleading figure.
   */
  serviceMix: ServiceSlice[];
  serviceMixTotal: number;
  todayAppointments: TodayAppointment[];
  lowStock: LowStockItem[];
  recentActivity: ActivityEntry[];
}

export const reportRangeSchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date_format'),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date_format'),
});
export type ReportRange = z.infer<typeof reportRangeSchema>;

export interface MonthlyRevenuePoint {
  month: string;
  billed: number;
  collected: number;
}

export interface RevenueReport {
  from: string;
  to: string;
  /** Collected in the range. */
  revenue: number;
  outstanding: number;
  paidInvoices: number;
  patientsBilled: number;
  /** Collected divided by distinct patients billed; 0 when there are none. */
  averagePerPatient: number;
  monthly: MonthlyRevenuePoint[];
  serviceMix: ServiceSlice[];
  serviceMixTotal: number;
}

/** Sums to 100 across the slices, guarding the empty case. */
export function sliceShare(amount: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((amount / total) * 1000) / 10;
}

/** One drug's row in the stock in/out report (docs/spec.md §14 round 4, F7). */
export interface StockMovementRow {
  drugId: number;
  nameLa: string;
  unit: string;
  received: number;
  sold: number;
  returned: number;
  adjusted: number;
  writtenOff: number;
  net: number;
  qtyOnHand: number;
}

export interface StockMovementReport {
  from: string;
  to: string;
  totals: {
    received: number;
    sold: number;
    returned: number;
    adjusted: number;
    writtenOff: number;
    net: number;
  };
  byDrug: StockMovementRow[];
}

/**
 * Where a drug line's revenue came from — the billing counter (an invoice
 * line with `sourceType: 'drug'`) or the pharmacy till (a `sale_items` row).
 * Kept distinct rather than merged per drug so the report can say which
 * channel a given profit figure came from (docs/spec.md §14 round 4, F8).
 */
export type ProfitSource = 'invoice' | 'sale';

export interface ProfitFigures {
  revenue: number;
  cost: number;
  profit: number;
}

export interface ProfitRow extends ProfitFigures {
  drugId: number;
  nameLa: string;
  unit: string;
  source: ProfitSource;
  qty: number;
  /** Current cost/unit used for the calculation — no per-sale cost snapshot is kept. */
  costPrice: number;
}

export interface ProfitReport {
  from: string;
  to: string;
  totals: ProfitFigures;
  bySource: Record<ProfitSource, ProfitFigures>;
  rows: ProfitRow[];
}
